import React, { useState } from 'react';
import axios from 'axios';
import { Mail, CheckCircle2, Send } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import WhatsAppFallback from './WhatsAppFallback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterCapture = ({ source = 'blog', variant = 'card' }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email, source });
      if (res.data?.already_subscribed) {
        toast.success("You're already on our list — thanks!");
      } else {
        toast.success('Subscribed! Check your inbox for our next Bangalore pool guide.');
      }
      setSubscribed(true);
      setEmail('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'inline') {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-3 max-w-xl"
        data-testid="newsletter-inline-form"
      >
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="h-12 flex-1"
          data-testid="newsletter-inline-input"
        />
        <Button
          type="submit"
          disabled={loading || subscribed}
          className="h-12 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-semibold px-6"
          data-testid="newsletter-inline-submit"
        >
          {subscribed ? (
            <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Subscribed</>
          ) : loading ? 'Subscribing...' : (
            <><Send className="w-4 h-4 mr-1.5" /> Subscribe</>
          )}
        </Button>
      </form>
    );
  }

  return (
    <div
      className="my-12 rounded-2xl bg-gradient-to-br from-[#003366] to-[#001a33] text-white p-8 md:p-10 shadow-xl relative overflow-hidden"
      data-testid="newsletter-capture-card"
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#00AEEF]/15 rounded-full blur-2xl" />
      <div className="relative grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#00AEEF]/15 text-[#00AEEF] px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <Mail className="w-3.5 h-3.5" /> Free Pool Insights
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mb-2 leading-tight">
            Get Bangalore's Best Pool Tips — Free
          </h3>
          <p className="text-white/70 text-sm leading-relaxed">
            One short email a month: pool maintenance, design ideas, monsoon checklists, and Bangalore-specific cost insights. Unsubscribe anytime.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="newsletter-form">
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-[#00AEEF]"
            data-testid="newsletter-email-input"
          />
          <Button
            type="submit"
            disabled={loading || subscribed}
            className="w-full h-12 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-semibold"
            data-testid="newsletter-submit"
          >
            {subscribed ? (
              <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Subscribed</>
            ) : loading ? 'Subscribing...' : (
              <><Send className="w-4 h-4 mr-1.5" /> Subscribe Free</>
            )}
          </Button>
          <p className="text-xs text-white/40 text-center">No spam. We respect your inbox.</p>
          <div className="text-center pt-2">
            <WhatsAppFallback
              variant="compact"
              prefill="Hi Aquarist Pools, please add me to your monthly pool tips newsletter."
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsletterCapture;
